import { Box, Typography } from '@mui/material';
import { PublicApiAllowedOperation, PublicApiKeyItem } from '@provision/entity-types';
import { Controller, useForm } from 'react-hook-form';
import { FormFieldLabel, FormTextField } from 'src/components/Forms';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { useCreatePublicApiKey, useUpdatePublicApiKey } from 'src/graphql';
import { PermissionTable } from 'src/pages/Settings/integrations/developer/permission-table';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

export type PublicApiKeyModalProps =
  | {
      mode: 'create';
    }
  | {
      mode: 'edit';
      apiKey: PublicApiKeyItem;
      onUpdated?: (result: PublicApiKeyItem) => void;
    };

enum PublicApiKeyFormField {
  Name = 'name',
  AllowedOperations = 'allowedOperations',
}

interface FormValues {
  [PublicApiKeyFormField.Name]: string;
  [PublicApiKeyFormField.AllowedOperations]: PublicApiAllowedOperation[];
}

const EMPTY_FORM_VALUES: FormValues = {
  [PublicApiKeyFormField.Name]: '',
  [PublicApiKeyFormField.AllowedOperations]: [],
};

export const PublicApiKeyModal = () => {
  const { closeModal } = useModalMethods(Modals.PublicApiKey);
  const { isOpened, modalProps } = useModalState(Modals.PublicApiKey);
  const { createPublicApiKey, isCreating } = useCreatePublicApiKey();
  const { updatePublicApiKey, isUpdating } = useUpdatePublicApiKey();

  const isEdit = modalProps?.mode === 'edit';
  const isBusy = isCreating || isUpdating;
  const initialValues: FormValues =
    modalProps?.mode === 'edit'
      ? {
          [PublicApiKeyFormField.Name]: modalProps.apiKey.name,
          [PublicApiKeyFormField.AllowedOperations]: modalProps.apiKey.allowedOperations,
        }
      : EMPTY_FORM_VALUES;

  const formMethods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = formMethods;

  if (!modalProps) return null;

  const handleClose = () => {
    reset(EMPTY_FORM_VALUES);
    closeModal();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (modalProps.mode === 'create') {
      const trimmedName = values[PublicApiKeyFormField.Name].trim();
      const result = await createPublicApiKey({
        name: trimmedName,
        allowedOperations: values[PublicApiKeyFormField.AllowedOperations],
      });
      if (result) {
        reset(EMPTY_FORM_VALUES);
        closeModal();
      }
      return;
    }

    const result = await updatePublicApiKey({
      apiKeyId: modalProps.apiKey.apiKeyId,
      allowedOperations: values[PublicApiKeyFormField.AllowedOperations],
    });
    if (result) {
      modalProps.onUpdated?.(result);
      closeModal();
    }
  });

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title={isEdit ? 'Edit API key scopes' : 'Create API key'}
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'public-api-key-cancel' },
        {
          label: isEdit ? 'Save changes' : 'Create key',
          kind: 'primary',
          onClick: () => void onSubmit(),
          disabled: !isValid,
          loading: isBusy,
          loadingLabel: isEdit ? 'Saving…' : 'Creating key…',
          testId: 'public-api-key-submit',
        },
      ]}
      onClose={handleClose}
      data-testid="public-api-key-modal"
    >
      <GenericForm<FormValues> formMethods={formMethods}>
        <Typography variant="body2" color="text.secondary">
          {isEdit
            ? 'Update the permissions for this key. The secret is not changed.'
            : 'Name the key and choose permissions. The secret will be available from the keys list.'}
        </Typography>
        <FormTextField
          type="textfield"
          name={PublicApiKeyFormField.Name}
          label="Key name"
          disabled={isBusy || isEdit}
          showErrorText
          rules={
            isEdit
              ? undefined
              : {
                  validate: (value: string) =>
                    value.trim().length > 0 || 'Key name is required',
                }
          }
          textFieldProps={{ inputProps: { 'data-testid': 'public-api-key-name' } }}
        />
        <Box>
          <FormFieldLabel component="span">Permissions</FormFieldLabel>
          <Controller
            name={PublicApiKeyFormField.AllowedOperations}
            control={control}
            rules={{
              validate: (value) => value.length > 0 || 'Select at least one permission',
            }}
            render={({ field }) => (
              <PermissionTable initialOperations={field.value} onChange={field.onChange} />
            )}
          />
          {errors[PublicApiKeyFormField.AllowedOperations] ? (
            <Typography variant="caption" color="error">
              {errors[PublicApiKeyFormField.AllowedOperations].message}
            </Typography>
          ) : null}
        </Box>
      </GenericForm>
    </ModalContainer>
  );
};
